// GovMap API Service for getting Israeli land registry information (גוש/חלקה)
// 
// Real API Integration Status: ✅ IMPLEMENTED
// - Uses actual GovMap API endpoints discovered through reverse engineering
// - Step 1: POST to /api/search-service/autocomplete to get coordinates
// - Step 2: GET from /api/layers-catalog/apps/parcel-search/address/ to get parcel data
// - Fallback: Intelligent mock data if CORS blocks requests or API fails
//
// Note: CORS may block direct calls from browser to GovMap.gov.il
// For production, consider using a proxy server or server-side implementation

import config from '../config/api';

class GovMapService {
  static GOVMAP_BASE_URL = 'https://www.govmap.gov.il';

  /** Fewer layers = faster entitiesByPoint; full set via localStorage urbanix_full_govmap_layers=1 */
  static ESSENTIAL_LAYER_IDS = [11, 125, 6, 218044, 210781];

  static LAYER_IDS = {
    RETZEFMIGRASHIM: 11,        // **KEY** Detailed land use designations (יעוד קרקע)
    TABA_BLUE_LINES: 203,
    LAND_USE: 14,
    OWNERSHIP: 6,
    SOCIO_ECONOMIC: 218044,
    BUILDING_STATS: 210781,
    PLANS_IN_PREP: 50,
    BUILDING_CURRENCY: 65,
    REGIONAL_AUTHORITIES: 125
  };

  /**
   * Search for address and get גוש/חלקה information
   * @param {string} address - The address to search for
   * @returns {Promise<Object>} - Object containing gush, helka, and other property details
   */
  static async getPropertyInfo(address) {
    console.log('🏠 Searching for property info:', address);
    
    try {
      // Attempt to get real data from GovMap (likely to fail without proper API access)
      const realData = await this.attemptRealGovMapData(address);
      if (realData) {
        console.log('✅ Real GovMap data retrieved:', realData);
        return realData;
      }
    } catch (error) {
      console.log('⚠️ GovMap API access failed (expected):', error.message);
    }
    
    // Generate intelligent mock data based on address patterns
    console.log('🎭 Generating intelligent mock data...');
    return this.getIntelligentMockData(address);
  }

  /**
   * Fetch data from advanced GovMap layers
   */
  static getAdvancedLayerIdsForRequest() {
    if (
      typeof window !== 'undefined' &&
      window.localStorage?.getItem('urbanix_full_govmap_layers') === '1'
    ) {
      return Object.values(this.LAYER_IDS);
    }
    return [...this.ESSENTIAL_LAYER_IDS];
  }

  /**
   * Unwrap GovMap parcel-search locate response coordinates (WM polygon ring).
   */
  static extractLocateParcelRingWm(locateData) {
    const c = locateData?.coordinates;
    if (!c || !Array.isArray(c)) return null;
    let ring = c;
    let depth = 0;
    while (Array.isArray(ring?.[0]) && typeof ring[0][0] !== 'number' && depth < 8) {
      ring = ring[0];
      depth += 1;
    }
    if (!Array.isArray(ring) || ring.length < 4) return null;
    const first = ring[0];
    if (!Array.isArray(first) || first.length < 2 || typeof first[0] !== 'number') return null;
    return ring;
  }

  static async fetchAdvancedLayers(coordinates) {
    try {
        const layerIds = this.getAdvancedLayerIdsForRequest();
        console.log('🔍 Fetching advanced GovMap layers for:', coordinates);
        console.log('📊 Layer IDs to fetch:', layerIds);
        
        const response = await fetch(`${config.apiBaseUrl}/api/govmap-layers`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                x: coordinates.x,
                y: coordinates.y,
                layerIds: layerIds
            })
        });

        if (!response.ok) {
            console.warn('⚠️ Advanced layers fetch failed:', response.status);
            return null;
        }

        const data = await response.json();
        console.log('✅ Advanced layers received:', Object.keys(data));
        console.log('🔍 Advanced layers landUse:', data.landUse);
        console.log('🔍 Full advanced layers data:', JSON.stringify(data, null, 2));
        // API already returns processed data, use directly
        return data;
    } catch (error) {
        console.error('❌ Error fetching advanced layers:', error);
        return null;
    }
  }

  /**
   * Process raw layer data into structured object
   */
  static processAdvancedLayers(responseData) {
    if (!responseData || !responseData.data) return null;

    const result = {
        urbanRenewal: [],
        planning: [],
        ownership: [],
        socioEconomic: null,
        building: null,
        antiquities: null,
        landUse: null  // Add land use field
    };

    responseData.data.forEach(layer => {
        // Map by layer name or ID since we know them
        // **KEY LAYER** - retzefmigrashim has detailed יעוד
        if (layer.name === 'retzefmigrashim') {
            if (layer.entities && layer.entities.length > 0) {
                const entity = layer.entities[0];
                const fields = {};
                if (entity.fields) {
                    entity.fields.forEach(f => {
                        if (f.fieldValue !== null && f.fieldValue !== undefined) {
                            fields[f.fieldName] = f.fieldValue;
                        }
                    });
                }
                if (fields['יעוד']) {
                    result.landUse = {
                        designation: fields['יעוד'],  // e.g., "מגורים ב"
                        code: fields['קוד יעוד'],
                        plan: fields['תוכנית'],
                        parcel: fields['מגרש'],
                        source: 'retzefmigrashim',
                        confidence: 'high'
                    };
                }
            }
        }
        // urbanrenewal_settlment
        else if (layer.name === 'urbanrenewal_settlment') {
            result.urbanRenewal = layer.entities || [];
        }
        // mehoziot_app_taba (Blue Lines)
        else if (layer.name === 'mehoziot_app_taba') {
            result.planning = [...result.planning, ...(layer.entities || [])];
        }
        // mehoziot_app_yk (Land Use)
        else if (layer.name === 'mehoziot_app_yk') {
             // Often duplicates or complements Taba, can merge or keep separate
             // For now, let's add them to planning but maybe mark source
             const entities = (layer.entities || []).map(e => ({...e, source: 'land_use'}));
             result.planning = [...result.planning, ...entities];
        }
        // parcel_ownership_new
        else if (layer.name === 'parcel_ownership_new') {
            result.ownership = layer.entities || [];
        }
        // layer_218044 (Socio Economic)
        else if (layer.name === 'layer_218044' && layer.entities?.length > 0) {
            result.socioEconomic = layer.entities[0];
        }
        // potential_energy_build_complex (Building Stats)
        else if (layer.name === 'potential_energy_build_complex' && layer.entities?.length > 0) {
            result.building = layer.entities[0];
        }
        // atikot_sites_itm
        else if (layer.name === 'atikot_sites_itm' && layer.entities?.length > 0) {
            result.antiquities = layer.entities[0];
        }
    });

    return result;
  }

  
  /**
   * Attempt to get real data from GovMap using discovered API endpoints
   */
  static async attemptRealGovMapData(address) {
    try {
      console.log('🔍 Attempting real GovMap lookup for:', address);
      
      // Step 1: Search for address to get coordinates
      const coordinates = await this.getCoordinatesFromGovMap(address);
      if (!coordinates) {
        console.log('❌ Could not get coordinates from GovMap');
        return null;
      }
      
      console.log('📍 Got coordinates:', coordinates);
      
      // Steps 2–3 in parallel: parcel locate + advanced layers (same coordinates; independent upstream calls)
      const parcelLocatePromise = (async () => {
        let data = await this.getParcelDataByCoordinates(coordinates);
        if (!data) {
          console.log('⚠️ No parcel found with standard tolerance, retrying with higher tolerance...');
          data = await this.getParcelDataByCoordinates(coordinates, 50);
        }
        return data;
      })();

      const advancedLayersPromise = this.fetchAdvancedLayers(coordinates).catch((err) => {
        console.warn('⚠️ Advanced layers fetch failed (parallel path):', err?.message);
        return null;
      });

      const [parcelData, advancedData] = await Promise.all([
        parcelLocatePromise,
        advancedLayersPromise,
      ]);

      if (!parcelData) {
        console.log('❌ Could not get parcel data from GovMap even with higher tolerance');
        return null;
      }

      console.log('🏠 Got parcel data:', parcelData);
      console.log('🏛️ Got advanced data:', advancedData ? Object.keys(advancedData) : '(none)');
      
      // Step 4: Format the response
      return {
        gush: parcelData.gushnumber?.toString(),
        helka: parcelData.parcelnumber?.toString(),
        subHelka: null, // Not provided by this API
        municipality: advancedData?.municipality || null,
        fullAddress: address,
        searchAddress: address, // The Hebrew address used for search
        coordinates: coordinates, // Include coordinates for Tel Aviv GIS
        advancedLayers: advancedData, // Include all advanced layer data
        locateParcelRingWm: parcelData.locateParcelRingWm || null,
        govmapParcelObjectId: parcelData.objectid ?? null,
        isMockData: false,
        confidence: 0.95, // High confidence for real data
        dataSource: 'GovMap API'
      };
      
    } catch (error) {
      console.error('❌ Real GovMap lookup failed:', error);
      return null;
    }
  }

  /**
   * Get coordinates from GovMap search API via Vercel Function
   */
  static async getCoordinatesFromGovMap(address) {
    try {
      const requestBody = {
        searchText: address,
        language: 'he',
        isAccurate: false,
        maxResults: 10
      };
      
      console.log('🌍 GovMap search request:', requestBody);
      
      const response = await fetch(`${config.apiBaseUrl}/api/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        console.log('⚠️ GovMap search failed with status:', response.status);
        const errorText = await response.text();
        console.log('⚠️ GovMap error response:', errorText);
        return null;
      }

      const data = await response.json();
      console.log('🔍 GovMap search response:', data);
      
      if (!data.results || data.results.length === 0) {
        console.log('⚠️ No results found in GovMap response');
        return null;
      }

      if (data.results && data.results.length > 0) {
        // Pick the result whose CITY matches the address. GovMap returns
        // matches across cities for street names shared between them (e.g.
        // "שלמה מולכו" exists in Rishon LeZion, Tel Aviv AND Jerusalem), and
        // results[0] is frequently the wrong city. The city is the last
        // comma-segment of the address; govmap encodes it (often abbreviated)
        // at the END of result.text. Falls back to results[0] if no match,
        // so this can only improve resolution, never regress it.
        const CITY_TEXT_ALIASES = {
          'ירושלים': ['ירושלים'],
          'תל אביב-יפו': ['תא', 'תל אביב', 'יפו'],
          'תל אביב': ['תא', 'תל אביב'],
          'ראשון לציון': ['RISHON', 'ראשון לציון'],
          'באר שבע': ['בש', 'באר שבע'],
          'חיפה': ['חיפה', 'HAIFA'],
          'חולון': ['חולון'],
          'בת ים': ['בת ים'],
          'רמת גן': ['רמת גן'],
          'פתח תקווה': ['פתח תקווה'],
          'נתניה': ['נתניה'],
          'גבעתיים': ['גבעתיים'],
        };
        const norm = (s) => String(s || '').toUpperCase().trim();
        const addrParts = String(address || '').split(',').map((s) => s.trim()).filter(Boolean);
        const city = addrParts.length > 1 ? addrParts[addrParts.length - 1] : '';
        const tokens = city ? (CITY_TEXT_ALIASES[city] || [city]) : [];
        const cityMatch = tokens.length
          ? data.results.find((r) => {
              const t = norm(r.text || r.originalText);
              return tokens.some((tok) => t.endsWith(norm(tok)));
            })
          : null;
        if (cityMatch) {
          console.log('🌍 GovMap city-matched result for', city, ':', cityMatch.text);
        }
        const result = cityMatch || data.results[0];
        if (result.shape && result.shape.startsWith('POINT(')) {
          // Parse coordinates from "POINT(3871247.2421512865 3774583.4819370178)"
          const coords = result.shape.match(/POINT\(([0-9.]+)\s+([0-9.]+)\)/);
          if (coords && coords.length === 3) {
            return {
              x: parseFloat(coords[1]),
              y: parseFloat(coords[2])
            };
          }
        }
      }

      return null;
    } catch (error) {
      console.error('❌ GovMap coordinate lookup error:', error);
      return null;
    }
  }

  /**
   * Get parcel data using coordinates from GovMap via Vercel Function
   */
  static async getParcelDataByCoordinates(coordinates, tolerance = 10) {
    try {
      const { x, y } = coordinates;
      
      console.log('🔍 Fetching parcel data for coordinates:', { x, y, tolerance });

      const response = await fetch(`${config.apiBaseUrl}/api/geocoding?action=locate&x=${x}&y=${y}&tolerance=${tolerance}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        console.log('⚠️ GovMap parcel lookup failed with status:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('🏠 GovMap parcel response:', data);

      if (data.properties && data.properties.gushnumber && data.properties.parcelnumber) {
        return {
          gushnumber: data.properties.gushnumber,
          parcelnumber: data.properties.parcelnumber,
          objectid: data.properties.objectid,
          locateParcelRingWm: this.extractLocateParcelRingWm(data),
        };
      }

      return null;
    } catch (error) {
      console.error('❌ GovMap parcel lookup error:', error);
      return null;
    }
  }

  /**
   * Get municipality name using coordinates from GovMap
   */
  static async getMunicipalityByCoordinates(coordinates) {
    try {
      const { x, y } = coordinates;
      
      console.log('🏛️ Fetching municipality for coordinates:', { x, y });

      const response = await fetch(`${config.apiBaseUrl}/api/geocoding?action=municipality&x=${x}&y=${y}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        console.log('⚠️ GovMap municipality lookup failed with status:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('🏛️ GovMap municipality response:', data);

      if (data.municipality) {
        return {
          municipality: data.municipality
        };
      }

      return null;
    } catch (error) {
      console.error('❌ GovMap municipality lookup error:', error);
      return null;
    }
  }
  
  /**
   * Generate intelligent mock data based on Israeli address patterns
   */
  static getIntelligentMockData(address) {
    const city = this.extractCity(address);
    const neighborhood = this.extractNeighborhood(address, city);
    const streetNumber = this.extractStreetNumber(address);
    
    // Generate realistic גוש and חלקה based on city and area
    const { gush, helka, subHelka } = this.generateRealisticGushHelka(city, neighborhood, streetNumber);
    
    return {
      gush: gush.toString(),
      helka: helka.toString(),
      subHelka: subHelka ? subHelka.toString() : null,
      municipality: city !== 'לא זוהה' ? city : null,
      fullAddress: address,
      searchAddress: address, // Store search address for UI display
      isMockData: true,
      confidence: this.calculateConfidence(address)
    };
  }
  
  /**
   * Generate realistic גוש/חלקה numbers based on actual Israeli cadastral patterns
   */
  static generateRealisticGushHelka(city, neighborhood, streetNumber) {
    // Real Israeli גוש ranges by city (approximate)
    const cityGushRanges = {
      'תל אביב-יפו': { min: 30000, max: 31500 },
      'ירושלים': { min: 30001, max: 30900 },
      'חיפה': { min: 10001, max: 11500 },
      'באר שבע': { min: 38001, max: 39500 },
      'פתח תקווה': { min: 4001, max: 4800 },
      'נתניה': { min: 8001, max: 8900 },
      'חולון': { min: 30501, max: 30700 },
      'רמת גן': { min: 30351, max: 30450 },
      'ראשון לציון': { min: 30801, max: 30950 },
      'רעננה': { min: 7501, max: 7650 }
    };
    
    const range = cityGushRanges[city] || { min: 20000, max: 25000 };
    const gush = Math.floor(Math.random() * (range.max - range.min) + range.min);
    
    // חלקה numbers are typically 1-300, with most being under 150
    const helka = Math.floor(Math.random() * 150) + 1;
    
    // תת-חלקה (sub-parcel) exists in about 30% of cases
    const subHelka = Math.random() > 0.7 ? Math.floor(Math.random() * 20) + 1 : null;
    
    return { gush, helka, subHelka };
  }
  
  /**
   * Calculate confidence level for mock data based on address completeness
   */
  static calculateConfidence(address) {
    let confidence = 0.6; // Base confidence for mock data
    
    if (address.includes('רחוב') || address.includes('שדרות')) confidence += 0.1;
    if (/\d+/.test(address)) confidence += 0.1; // Has street number
    if (address.includes(',')) confidence += 0.1; // Has city separator
    if (address.length > 20) confidence += 0.1; // Detailed address
    
    return Math.min(confidence, 0.9); // Max 90% confidence for mock data
  }
  
  /**
   * Extract street number from address
   */
  static extractStreetNumber(address) {
    const numberMatch = address.match(/\d+/);
    return numberMatch ? parseInt(numberMatch[0]) : null;
  }
  
  /**
   * Extract neighborhood from address string based on city and street patterns
   */
  static extractNeighborhood(address, city) {
    // City-specific neighborhood mapping
    const cityNeighborhoods = {
      'תל אביב-יפו': [
        'מרכז העיר', 'נווה צדק', 'פלורנטין', 'רובע 1', 'שכונת מונטיפיורי',
        'כרם התימנים', 'נחלת בנימין', 'שכונת שפירא', 'יפו העתיקה', 'צפון תל אביב'
      ],
      'ירושלים': [
        'מרכז העיר', 'גבעת שאול', 'הר נוף', 'רמת שלמה', 'מלחה',
        'בית הכרם', 'קטמון', 'תלפיות', 'גילה', 'ארנונה'
      ],
      'חיפה': [
        'עיר תחתית', 'הדר הכרמל', 'כרמל צרפתי', 'אחוזה', 'נווה שאנן',
        'רמת אלמוגי', 'דניה', 'קרית שפרינצק', 'עיר עליון', 'כבביר'
      ],
      'באר שבע': [
        'מרכז העיר', 'שכונה ד', 'רמות', 'שכונות הקמפוס', 'שכונה ה',
        'נווה זאב', 'נווה מנחם', 'רמת חן', 'שכונת שחר', 'תל שבע'
      ]
    };
    
    const neighborhoods = cityNeighborhoods[city] || [
      'מרכז העיר', 'צפון העיר', 'דרום העיר', 'מזרח העיר', 'מערב העיר'
    ];
    
    // Try to match specific street patterns to neighborhoods
    if (address.includes('דיזנגוף') || address.includes('בן יהודה')) return 'מרכز העיר';
    if (address.includes('רוטשילד') || address.includes('שבזי')) return 'נווה צדק';
    if (address.includes('ויטל') || address.includes('סלמה')) return 'פלורנטין';
    
    // Default to random neighborhood for the city
    return neighborhoods[Math.floor(Math.random() * neighborhoods.length)];
  }
  
  /**
   * Extract city from address string with improved pattern matching
   */
  static extractCity(address) {
    const cityPatterns = [
      { patterns: ['תל אביב', 'תל-אביב', 'Tel Aviv', 'ת"א', 'יפו'], city: 'תל אביב-יפו' },
      { patterns: ['ירושלים', 'Jerusalem', 'יירושלים'], city: 'ירושלים' },
      { patterns: ['חיפה', 'Haifa'], city: 'חיפה' },
      { patterns: ['באר שבע', 'באר-שבע', 'Beer Sheva'], city: 'באר שבע' },
      { patterns: ['פתח תקווה', 'פתח-תקווה', 'Petah Tikva'], city: 'פתח תקווה' },
      { patterns: ['נתניה', 'Netanya'], city: 'נתניה' },
      { patterns: ['חולון', 'Holon'], city: 'חולון' },
      { patterns: ['רמת גן', 'רמת-גן', 'Ramat Gan'], city: 'רמת גן' },
      { patterns: ['ראשון לציון', 'ראשל"צ', 'Rishon LeZion'], city: 'ראשון לציון' },
      { patterns: ['אופקים', 'Ofakim'], city: 'אופקים' },
      { patterns: ['שדרות', 'Sderot'], city: 'שדרות' },
      { patterns: ['נתיבות', 'Netivot'], city: 'נתיבות' },
      { patterns: ['אשקלון', 'Ashkelon'], city: 'אשקלון' },
      { patterns: ['מודיעין', 'Modiin'], city: 'מודיעין-מכבים-רעות' },
      { patterns: ['רעננה', 'Ra\'anana'], city: 'רעננה' },
      { patterns: ['הרצליה', 'Herzliya'], city: 'הרצליה' },
      { patterns: ['כפר סבא', 'Kfar Saba'], city: 'כפר סבא' },
      { patterns: ['אשדוד', 'Ashdod'], city: 'אשדוד' }
    ];
    
    for (const { patterns, city } of cityPatterns) {
      for (const pattern of patterns) {
        if (address.toLowerCase().includes(pattern.toLowerCase())) {
          return city;
        }
      }
    }
    
    return 'לא זוהה';
  }
}

export default GovMapService;
